import { useState } from 'react'
import { NavLink } from 'react-router'
import { FiArrowRight, FiShield, FiCopy, FiCheck } from 'react-icons/fi'
import { SiPaypal } from 'react-icons/si'
import { Container, Reveal, RevealGroup, HoverText } from '@/components/common'
import { tiers, PAYPAL_EMAIL } from '@/constants/donate'

export const DonateOptions = () => {
  const [copied, setCopied] = useState(false)

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(PAYPAL_EMAIL)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access denied or unavailable; the email is still shown as plain text above.
    }
  }
  return (
    <section className="bg-white py-24 lg:py-32">
      <Container>
        <div className="max-w-3xl">
          <Reveal variant="up" delay={100}>
            <h2 className="mt-7 text-4xl font-bold leading-[1.05] tracking-[-0.02em] text-ibon-dark sm:text-5xl lg:text-6xl">
              Choose how you&apos;d <span className="text-ibon-dark/30">like to help.</span>
            </h2>
          </Reveal>
        </div>

        <RevealGroup className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" step={90}>
          {tiers.map(({ Icon, title, text, cta, to }) => (
            <article
              key={title}
              className="group flex flex-col border border-border p-8 transition-colors hover:border-ibon-sky"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-surface text-ibon-sky transition-colors group-hover:bg-ibon-sky group-hover:text-ibon-dark">
                <Icon size={24} />
              </span>
              <h3 className="mt-7 text-xl font-bold tracking-tight text-ibon-dark">{title}</h3>
              <p className="mt-3 flex-1 text-base leading-relaxed text-text-muted">{text}</p>
              {to.startsWith('#') ? (
                <a
                  href={to}
                  className="group/btn mt-8 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-ibon-dark transition-colors hover:text-ibon-sky"
                >
                  <HoverText text={cta} />
                  <FiArrowRight
                    className="transition-transform duration-300 group-hover/btn:translate-x-1"
                    size={14}
                  />
                </a>
              ) : (
                <NavLink
                  to={to}
                  className="group/btn mt-8 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-ibon-dark transition-colors hover:text-ibon-sky"
                >
                  <HoverText text={cta} />
                  <FiArrowRight
                    className="transition-transform duration-300 group-hover/btn:translate-x-1"
                    size={14}
                  />
                </NavLink>
              )}
            </article>
          ))}
        </RevealGroup>

        {/* PayPal CTA */}
        <Reveal variant="up" delay={200}>
          <div
            id="give-now"
            className="mt-10 scroll-mt-24 border-2 border-ibon-sky bg-ibon-sky/5 p-8 sm:p-10"
          >
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-5">
                <SiPaypal className="shrink-0 text-5xl text-[#003087]" />
                <div>
                  <p className="text-lg font-bold tracking-tight text-ibon-dark">
                    Donate via PayPal
                  </p>
                  <p className="mt-1 text-sm text-text-muted">
                    Open PayPal → Send Money → paste the address below
                  </p>
                </div>
              </div>
              <a
                href="https://www.paypal.com/myaccount/transfer/homepage/send"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#003087] px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#001f5b]"
              >
                <HoverText text="Open PayPal" />
                <FiArrowRight
                  className="transition-transform duration-300 group-hover:translate-x-1"
                  size={15}
                />
              </a>
            </div>

            {/* Email copy row */}
            <div className="mt-6 flex items-center justify-between gap-4 border-t border-ibon-sky/20 pt-6">
              <p className="font-mono text-base font-semibold text-ibon-dark">{PAYPAL_EMAIL}</p>
              <button
                type="button"
                onClick={copyEmail}
                className="inline-flex items-center gap-2 rounded-full border border-ibon-sky px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ibon-dark transition-colors hover:bg-ibon-sky hover:text-white"
              >
                {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                {copied ? 'Copied!' : 'Copy email'}
              </button>
            </div>
          </div>
        </Reveal>

        {/* Transparency note */}
        <Reveal variant="up" delay={120}>
          <div className="mt-12 flex items-start gap-4 border-t border-border pt-8">
            <FiShield className="mt-0.5 shrink-0 text-ibon-sky" size={22} />
            <p className="max-w-2xl text-base leading-relaxed text-text-muted">
              Every contribution is managed with a firm commitment to{' '}
              <span className="font-semibold text-ibon-dark">transparency and accountability</span>,
              ensuring your support reaches the people and communities who need it most.
            </p>
          </div>
        </Reveal>
      </Container>
    </section>
  )
}
